import type { RegisteredComponent } from "@builder.io/sdk-react";

import { FeatureHero } from "~/hec/components/organisms/feature-hero/feature-hero";
import { toHeadingFont } from "~/hec/utils/heading-font";
import { toInlineHeadingHtml } from "~/hec/utils/rich-text";
import type { SiteId } from "~/hec/utils/site-id";
import { useRootLayoutData } from "~/root";

import { toFeatureLinkTarget } from "../feature-link/feature-link.logic";
import { toAlign, toHeadingTag } from "./feature-hero.logic";
import { featureHeroInputs } from "./inputs";
import type { BuilderFeatureHeroProps } from "./types";

/**
 * Feature Hero (COM-945): replaces the "Hero" symbol. Separate from the
 * COM-941 `Hero`. Thin wrapper: maps inputs, resolves the hero link and
 * spreads `attributes` on the root (`noWrap` contract, CLAUDE.md §4).
 */
const BuilderFeatureHero = ({
  desktopImage,
  mobileImage,
  eyebrow,
  logo,
  logoAlt,
  heading,
  headingTag,
  headingFont,
  subheading,
  buttonText,
  badge,
  badgeAlt,
  textBoxAlign,
  textAlign,
  textBoxBackgroundColor,
  priority,
  attributes,
  ...link
}: BuilderFeatureHeroProps) => {
  const { site } = useRootLayoutData();
  return (
    <FeatureHero
      link={toFeatureLinkTarget(link, site.id as SiteId)}
      desktopImage={desktopImage}
      mobileImage={mobileImage}
      eyebrow={eyebrow?.trim() || undefined}
      logo={logo || undefined}
      logoAlt={logoAlt}
      headingHtml={toInlineHeadingHtml(heading)}
      headingTag={toHeadingTag(headingTag)}
      headingFont={toHeadingFont(headingFont)}
      subheading={subheading?.trim() || undefined}
      buttonText={buttonText?.trim() || undefined}
      badge={badge || undefined}
      badgeAlt={badgeAlt}
      textBoxAlign={toAlign(textBoxAlign)}
      textAlign={toAlign(textAlign)}
      textBoxBackgroundColor={textBoxBackgroundColor}
      priority={priority !== false}
      {...attributes}
    />
  );
};

export const FeatureHeroDefinition = {
  component: BuilderFeatureHero,
  name: "FeatureHero",
  // Label only — safe to rename. `name` above is the published contract.
  friendlyName: "Feature Hero",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/panel-top.svg",
  inputs: featureHeroInputs,
} satisfies RegisteredComponent;

export default BuilderFeatureHero;
